import React, {useEffect, useMemo, useState} from 'react';
import {Modal} from 'react-bootstrap';
import OeTip from './OeTip';
import OeSelect from './OeSelect';
import {loadDruckmappeDocs, loadPrinters, previewDruckmappe, printDruckmappe} from './api';
import {WH_LAHR, splitPlan} from './wizard';

/**
 * Druckmaske des Auftrags-Editors (Ticket #130): Deckblatt, Bestellung und
 * Kommissionierschein als EIN kompaktes PDF ohne Logo, auf Wunsch mit
 * weclapp-Belegen (z. B. der AB) im selben Druckvorgang.
 *
 * Wortlaut: „Eine Maske wo man alles Drucken kann … (max 1 Blatt wenns geht)
 * Logo weg usw.“
 */
const TEILE = [
    {key: 'deckblatt', label: 'Deckblatt', tip: 'Kopfdaten, Kundenbemerkung aus dem Kundenstamm und Kommentar des Käufers aus dem Shop.'},
    {key: 'bestellung', label: 'Bestellung', tip: 'Positionen der Shop-Bestellung kompakt ohne Bilder und Preise; ohne Shop-Bestellung die Positionen des Auftrags.'},
    {key: 'komm', label: 'Kommissionierschein', tip: 'Die Lahr-Positionen zum Picken bzw. Kontroll-Zählen, mit Abhak-Kästchen.'},
];

function Schalter({on, onClick, children, tip, disabled}) {
    return (
        <OeTip tip={tip}>
            <button
                type="button"
                role="checkbox"
                aria-checked={on}
                className={`pmopt${on ? ' on' : ''}`}
                disabled={disabled}
                onClick={onClick}
            >
                <span className="pmbox">{on ? '✓' : ''}</span>
                {children}
            </button>
        </OeTip>
    );
}

export default function PrintMask({show, onClose, orderRef, order, rows, stock, canPrint, onToast}) {
    const [teile, setTeile] = useState({deckblatt: true, bestellung: true, komm: true});
    const [grouped, setGrouped] = useState(true);
    const [docs, setDocs] = useState(null);
    const [docSel, setDocSel] = useState({});
    const [printers, setPrinters] = useState([]);
    const [printer, setPrinter] = useState('');
    const [printerStandard, setPrinterStandard] = useState('');
    const [copies, setCopies] = useState('1');
    const [vorschau, setVorschau] = useState(null);
    const [laeuft, setLaeuft] = useState(null);
    const [status, setStatus] = useState(null);

    const plan = useMemo(() => (stock && stock.loaded ? splitPlan(WH_LAHR, rows, stock) : null), [rows, stock]);

    useEffect(() => {
        if (!show) {
            return undefined;
        }
        let ab = false;
        setStatus(null);
        loadDruckmappeDocs(orderRef)
            .then((d) => !ab && setDocs(d.docs || []))
            .catch(() => !ab && setDocs([]));
        if (canPrint) {
            loadPrinters(orderRef).then((p) => {
                if (ab) {
                    return;
                }
                const liste = (((p.os || [])[0]) || {}).printers || [];
                setPrinters(liste);
                const pref = p.preferred && liste.includes(p.preferred) ? p.preferred : null;
                setPrinterStandard(pref && p.preferredFixed ? pref : '');
                setPrinter(pref || liste.find((x) => /lager/i.test(x)) || liste[0] || '');
            }).catch(() => {});
        }
        return () => {
            ab = true;
        };
    }, [show, orderRef, canPrint]);

    // Vorschau-Adresse freigeben, wenn sie nicht mehr gebraucht wird.
    useEffect(() => () => {
        if (vorschau) {
            URL.revokeObjectURL(vorschau.url);
        }
    }, [vorschau]);

    const gewaehlteTeile = TEILE.filter((t) => teile[t.key]).map((t) => t.key);
    const gewaehlteDocs = (docs || []).filter((d) => docSel[d.id]).map((d) => d.id);

    const payload = () => ({
        teile: gewaehlteTeile,
        grouped,
        lahrItemIds: plan ? plan.primaryRows.map((r) => String(r.id)).filter((id) => /^\d+$/.test(id)) : [],
        schneiderItemIds: plan ? plan.secondaryRows.map((r) => String(r.id)).filter((id) => /^\d+$/.test(id)) : [],
        weclappDocIds: gewaehlteDocs,
    });

    const zeigen = async () => {
        setLaeuft('vorschau');
        setStatus(null);
        try {
            const {blob, pages} = await previewDruckmappe(orderRef, payload());
            setVorschau({url: URL.createObjectURL(blob), pages});
        } catch (e) {
            setStatus({tone: 'err', text: 'Vorschau fehlgeschlagen: ' + (e.response?.statusText || e.message)});
        } finally {
            setLaeuft(null);
        }
    };

    const drucken = async () => {
        setLaeuft('druck');
        setStatus(null);
        try {
            const j = await printDruckmappe(orderRef, {
                ...payload(),
                printerName: printer,
                quantity: Math.max(1, Math.min(20, parseInt(copies, 10) || 1)),
            });
            const n = (j.jobs || []).length;
            const text = j.dryRun
                ? `Trockenlauf — ${j.message || 'nichts an den Drucker geschickt.'}`
                : `✓ Druckmappe (${j.pages || '?'} Seite(n))${n > 1 ? ` und ${n - 1} Beleg(e)` : ''} an „${printer}" geschickt.`;
            setStatus({tone: j.dryRun ? 'warn' : 'ok', text});
            onToast(text);
        } catch (e) {
            const d = e.response?.data || {};
            setStatus({tone: d.blocked ? 'warn' : 'err', text: (d.blocked ? 'Testmodus: ' : 'Druck fehlgeschlagen: ') + (d.error || e.message)});
        } finally {
            setLaeuft(null);
        }
    };

    const nichts = gewaehlteTeile.length === 0;

    return (
        <Modal show={show} onHide={onClose} size="xl" centered aria-labelledby="oe-pm-title">
            <Modal.Header closeButton>
                <Modal.Title id="oe-pm-title" style={{fontSize: 16}}>
                    🖨 Druckmaske — {order ? order.number : ''}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="printmask">
                <div className="pmcols">
                    <div className="pmside">
                        <div className="pmh">In die Mappe (ein PDF, ohne Logo)</div>
                        {TEILE.map((t) => (
                            <Schalter
                                key={t.key}
                                on={!!teile[t.key]}
                                tip={t.tip}
                                onClick={() => setTeile((v) => ({...v, [t.key]: !v[t.key]}))}
                            >
                                {t.label}
                            </Schalter>
                        ))}
                        {teile.komm ? (
                            <div className="pmsub">
                                <OeTip tip="Zum Kontroll-Zählen: gleiche Gebinde nach Warengruppe gebündelt.">
                                    <button type="button" className={`pmseg${grouped ? ' on' : ''}`} aria-pressed={grouped} onClick={() => setGrouped(true)}>
                                        zusammengefasst
                                    </button>
                                </OeTip>
                                <OeTip tip="Zum Picken: jede Artikelnummer einzeln mit eigener Menge.">
                                    <button type="button" className={`pmseg${!grouped ? ' on' : ''}`} aria-pressed={!grouped} onClick={() => setGrouped(false)}>
                                        einzeln
                                    </button>
                                </OeTip>
                            </div>
                        ) : null}

                        <div className="pmh">Dazu drucken (weclapp-Belege)</div>
                        {docs === null ? <div className="muted">lädt …</div> : null}
                        {docs && !docs.length ? <div className="muted">Keine PDF-Belege am Auftrag.</div> : null}
                        {(docs || []).map((d) => (
                            <Schalter
                                key={d.id}
                                on={!!docSel[d.id]}
                                tip="Wird nach der Mappe im selben Druckvorgang an den Drucker geschickt (nicht in die Vorschau gemischt)."
                                onClick={() => setDocSel((v) => ({...v, [d.id]: !v[d.id]}))}
                            >
                                <span className="pmdoc">{d.name}</span>
                            </Schalter>
                        ))}

                        {canPrint ? (
                            <>
                                <div className="pmh">Drucker</div>
                                {printerStandard && printer === printerStandard ? (
                                    <OeTip tip="Dieser Drucker ist in deinem Benutzerkonto als Standarddrucker hinterlegt und immer vorausgewählt. Ändern kannst du ihn unter „Profil“ bzw. in der Benutzerverwaltung.">
                                        <span className="muted" style={{fontSize: '0.8em'}}>Standard aus deinem Benutzerkonto</span>
                                    </OeTip>
                                ) : null}
                                <OeSelect
                                    className="form-control form-control-sm"
                                    value={printer}
                                    options={printers.map((p) => ({value: p, label: p}))}
                                    onChange={(v) => setPrinter(String(v))}
                                    ariaLabel="Drucker"
                                    disabled={!printers.length}
                                />
                                <div className="pmcopies">
                                    <OeTip tip="Anzahl Ausdrucke je Dokument (1 bis 20).">
                                        <input
                                            inputMode="numeric"
                                            aria-label="Kopien"
                                            value={copies}
                                            onChange={(e) => setCopies(e.target.value.replace(/[^\d]/g, ''))}
                                        />
                                    </OeTip>
                                    <span className="muted">Kopien</span>
                                </div>
                            </>
                        ) : null}

                        <div className="pmactions">
                            <OeTip tip="Zeigt die Mappe rechts an. Es wird nichts gespeichert und nichts gedruckt.">
                                <button type="button" className="btn btn-sm btn-outline-secondary" disabled={nichts || !!laeuft} onClick={zeigen}>
                                    {laeuft === 'vorschau' ? '… erzeugt' : '👁 Vorschau'}
                                </button>
                            </OeTip>
                            {canPrint ? (
                                <OeTip
                                    tip="Hängt die Mappe als PDF an den Auftrag (ältere Fassung wird ersetzt) und schickt sie mit den gewählten Belegen an den Drucker."
                                    detail="Im Testmodus läuft der Druck als Trockenlauf; an echten Aufträgen wird dort nichts angehängt."
                                >
                                    <button type="button" className="btn btn-sm btn-weclapp" disabled={nichts || !printer || !!laeuft} onClick={drucken}>
                                        {laeuft === 'druck' ? '… druckt' : '🖨 An Drucker'}
                                    </button>
                                </OeTip>
                            ) : null}
                            {vorschau ? (
                                <OeTip tip="Öffnet die Vorschau in einem neuen Tab, z. B. für den Browser-Druck.">
                                    <a className="btn btn-sm btn-outline-secondary" href={vorschau.url} target="_blank" rel="noopener noreferrer">Browser-Druck</a>
                                </OeTip>
                            ) : null}
                        </div>
                        {status ? <div className={`pmstatus ${status.tone}`}>{status.text}</div> : null}
                    </div>
                    <div className="pmpreview">
                        {vorschau ? (
                            <>
                                {vorschau.pages ? <div className="muted pmpages">{vorschau.pages} Seite(n)</div> : null}
                                <iframe title="Vorschau der Druckmappe" src={vorschau.url}/>
                            </>
                        ) : (
                            <div className="pmempty muted">„Vorschau“ zeigt die Mappe hier an.</div>
                        )}
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}
